import React, { useEffect, useState } from "react";
import { X, Info, CheckCircle, AlertTriangle, XCircle } from "lucide-react";

/**
 * Professional reusable InfoBox component with persistence
 * For notifications and hints in Statistics with localStorage support
 */
const InfoBox = ({
  title,
  message,
  show,
  onDismiss,
  color = "blue",
  className = "",
  id, // Unique identifier for persistence
  dismissible = true,
  icon: CustomIcon,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Check if this info box was previously dismissed
  useEffect(() => {
    if (!id || !show) {
      setIsVisible(show);
      return;
    }

    const dismissedInfoBoxes = JSON.parse(
      localStorage.getItem("dismissedInfoBoxes") || "[]"
    );

    if (!dismissedInfoBoxes.includes(id)) {
      setIsVisible(show);
    } else {
      setIsVisible(false);
    }
  }, [show, id]);

  const handleDismiss = () => {
    setIsVisible(false);

    // Save to localStorage if ID is provided
    if (id) {
      const dismissedInfoBoxes = JSON.parse(
        localStorage.getItem("dismissedInfoBoxes") || "[]"
      );

      if (!dismissedInfoBoxes.includes(id)) {
        dismissedInfoBoxes.push(id);
        localStorage.setItem(
          "dismissedInfoBoxes",
          JSON.stringify(dismissedInfoBoxes)
        );
      }
    }

    if (onDismiss) {
      onDismiss();
    }
  };

  if (!isVisible) return null;

  // Icon mapping
  const iconMap = {
    blue: Info,
    green: CheckCircle,
    yellow: Info,
    red: XCircle,
  };

  const Icon = CustomIcon || iconMap[color] || Info;

  // Professional color schemes with red background and colored headers
  const colorClasses = {
    yellow: {
      container: "bg-primary/90 border-amber-400/30 shadow-test/10",
      title: "text-test",
      message: "text-lightGray",
      icon: "text-test",
      closeBtn: "text-gray-400 hover:text-test",
      accent: "border-l-tests",
    },
  };

  const colors = colorClasses[color];

  return (
    <div
      className={`
        mb-6 mt-4 border border-l-4 ${colors.container} ${colors.accent}
        backdrop-blur-sm shadow-lg rounded-lg 
        transition-all duration-300 ease-in-out
        hover:shadow-xl hover:border-opacity-50
        ${className}
      `}
    >
      <div className="flex items-start p-4 space-x-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${colors.icon} mt-0.5`}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm ${colors.title} mb-1`}>
            {title}
          </h4>
          <p className={`text-sm leading-relaxed ${colors.message}`}>
            {message}
          </p>
        </div>

        {/* Dismiss Button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className={`
              flex-shrink-0 ${colors.closeBtn}
              transition-colors duration-200 p-1 rounded-md
              hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20
            `}
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default InfoBox;
